import React, { useEffect } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { useStoreState } from "src/pages/contentMap/index";
import { useSpring, animated } from "@react-spring/web";

import { CONTENT_MAP_FILTERS, SUBFILTERS } from "src/constants/contentMap";
import { guid } from 'src/utils/util';

import {
  Cluster,
  COLLECTION,
  DEAL_ROOM,
  COMPANY,
  CONTACT,
  Filter,
  FilterActionEnum,
  ModuleType,
  Operators,
  PropertyType,
  USER_GROUP,
} from "src/types/clusterFilters";
import { CustomField } from "src/types/customFields";
import Reveal from "src/components/animation";

import { REPORTS_FILTERS } from "src/constants/reports";
import InputBox from "./filterInput";

import userRoleService from "src/utils/userRoles.service";

import _ from "lodash";
import cx from "classnames";
import styles from "./cluster.module.scss";

interface ClusterViewProps {
  cluster?: Cluster | null;
  handleFilterUpdate: (filters: Filter[], shouldFetchAssets: boolean) => void;
  moduleType: ModuleType;
  onSearchTrack?: (searchString: string) => void; 
  onClose?: () => void;
}

interface FilterContextProps {
  filters: Filter[];
  handlePrimaryFilterChange: (id: string, item: Entity | CustomField) => void;
  handleSubFilterChange: (id: string, item: SubFilterType) => void;
  handleValueChange: (id: string, value: any) => void;
  handleExpiryChange: (
    id: string,
    value: any,
    expirationDate: String[],
  ) => void;
  handleDeleteFilter: (id: string) => void;
  PRIMARY_FILTERS: { name: string; value: string; filter_type: string }[];
}

export const FilterContext = React.createContext<FilterContextProps>({
  filters: [],
  handlePrimaryFilterChange: () => { },
  handleSubFilterChange: () => { },
  handleValueChange: () => { },
  handleExpiryChange: () => { },
  handleDeleteFilter: () => { },
  PRIMARY_FILTERS: []
});

const ClusterView: React.FC<ClusterViewProps> = (props) => {
  const { cluster, handleFilterUpdate , onSearchTrack} = props;
  const [filters, setFilters] = React.useState<Filter[]>([]);
  const [clusterAction, setClusterAction] = React.useState<FilterActionEnum>(
    FilterActionEnum.none,
  );

  //Displaying the filters according to the module type
  const getPrimaryFilters = (moduleType: ModuleType) => {
    switch (moduleType) {
      case ModuleType.CONTENT_MAP:
        return CONTENT_MAP_FILTERS;
      case ModuleType.REPORTS:
      default: {
        return REPORTS_FILTERS.filter(({ value }) => {
          if (value === COLLECTION && !userRoleService.canAccessCollection()) return false;
          if (value === DEAL_ROOM && !userRoleService.canAccessDealRoom()) return false;
          if ((value === CONTACT || value === COMPANY) && !userRoleService.canShare()) return false;
          if (value === USER_GROUP && userRoleService.canRestrictAllReports()) return false;
          return true;
        });
      }
    }
  };

  const PRIMARY_FILTERS = getPrimaryFilters(props.moduleType);

  useEffect(() => {
    if (_.size(_.get(cluster, "filters", [])) > 0) {
      const clusterFilters = _.get(cluster, "filters", []);
      var filtersWithId = clusterFilters
        ? clusterFilters.map((filter: Filter) => {
          return {
            ...filter,
            id: guid(),
          };
        })
        : [];
      setFilters(filtersWithId);
    } else {
      addNewFilter([]);
    }
  }, [cluster]);

  useEffect(() => {
    const extractedFilters = filters.map((filter) => {
      const property = _.get(filter, "property.type");
      var value = _.get(filter, "value", []);
      if (
        property === "asset" ||
        property === "stream" ||
        property === "group"
      ) {
        value = _.map(value, "id");
      }
      var updatedFilter = filter;
      updatedFilter = {
        ...filter,
        value: value,
      };
      return updatedFilter;
    });

    if (_.size(filters) > 0) {
      const shouldFetchAssets = !_.includes(
        [FilterActionEnum.add_new_filter, FilterActionEnum.primary_filter],
        clusterAction,
      );
      handleFilterUpdate(extractedFilters, shouldFetchAssets);
    }
    setClusterAction(FilterActionEnum.none);
  }, [filters]);

  const addNewFilter = (prevFilters: Filter[]) => {
    const newFilter = {
      operator: Operators.is,
      property: { type: PropertyType.asset },
      value: [],
      expiryDateFilter: null,
      id: guid(),
    };
    setFilters([...prevFilters, newFilter]);
    if (_.size(prevFilters) > 0) {
      setClusterAction(FilterActionEnum.add_new_filter);
    }
  };

  const handlePrimaryFilterChange = (
    id: string,
    item: Entity | CustomField,
  ) => {
    const isCustomFieldSelected = !_.isEqual(
      _.get(item, "filter_type", ""),
      "entity_filter",
    );
    const subFilter = isCustomFieldSelected
      ? getSubFilter("custom_field")
      : getSubFilter(_.get(item, "value", ""));

    const updatedFilters = filters.map((filter) => {
      if (filter.id === id) {
        var updatedFilter = filter;
        if (isCustomFieldSelected) {
          updatedFilter = {
            ...filter,
            property: {
              type: PropertyType.custom_field,
              fieldId: _.get(item, "id"),
            },
            operator: subFilter.value as Operators,
            expiryDateFilter: null,
            value: [],
          };
        } else {
          updatedFilter = {
            ...filter,
            property: {
              type: _.get(item, "value") as PropertyType,
            },
            operator: subFilter.value as Operators,
            expiryDateFilter: null,
            value: [],
          };
        }
        return updatedFilter;
      }
      return filter;
    });
    setFilters(updatedFilters);
    setClusterAction(FilterActionEnum.primary_filter);
  };

  const handleSubFilterChange = (id: string, item: SubFilterType) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.id === id) {
        var updatedFilter = filter;
        updatedFilter = {
          ...filter,
          operator: item.value as Operators,
        };
        return updatedFilter;
      }
      return filter;
    });
    onSearchTrack?.(String(updatedFilters[0].operator || ""));
    setFilters(updatedFilters);
    setClusterAction(FilterActionEnum.sub_filter);
  };

  const handleValueChange = (id: string, value: any) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.id === id) {
        var updatedFilter = filter;
        updatedFilter = {
          ...filter,
          value: value,
        };
        return updatedFilter;
      }
      return filter;
    });

    const queries = _.map(updatedFilters[0].value, "name");

    onSearchTrack?.(queries.join(", "));
    setFilters(updatedFilters);
    setClusterAction(FilterActionEnum.input_value);
  };

  const handleExpiryChange = (
    id: string,
    value: any,
    expirationDate: String[],
  ) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.id === id) {
        var updatedFilter = filter;
        updatedFilter = {
          ...filter,
          expiryDateFilter: value,
          value: expirationDate,
        };
        return updatedFilter;
      }
      return filter;
    });

    setFilters(updatedFilters);
    setClusterAction(FilterActionEnum.expiry_date);
    onSearchTrack?.(String(updatedFilters[0].expiryDateFilter || ""));
  };

  const getSubFilter = (value: string) => {
    if (_.size(value) > 0) {
      const extractedSubFilters = _.filter(SUBFILTERS, (subFilter) => {
        return _.includes(subFilter.type, value);
      });

      return extractedSubFilters[0];
    }
    return SUBFILTERS[0];
  };

  const handleDeleteFilter = (id: string) => {
    if (_.size(filters) === 1) {
      handlePrimaryFilterChange(id, PRIMARY_FILTERS[0]);
    } else {
      const updatedFilters = filters.filter((filter) => filter.id !== id);
      setFilters(updatedFilters);
    }
    setClusterAction(FilterActionEnum.delete_filter);
  };

  const contextValues = {
    filters,
    handlePrimaryFilterChange,
    handleSubFilterChange,
    handleValueChange,
    handleExpiryChange,
    handleDeleteFilter,
    PRIMARY_FILTERS,
    type: props.moduleType,
  };

  const springs = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
  });

  return (
    <div className={styles.filtersContainer}>
      <FilterContext.Provider value={contextValues}>
        {filters.map((filter: Filter) => (
          <FilterComponent key={filter.id} filter={filter} moduleType={props.moduleType} />
        ))}
      </FilterContext.Provider>

      <Reveal show={_.size(filters) > 0}>
        <div
          onClick={() => addNewFilter(filters)}
          className={styles.addListButton}
        >
          <i className={cx(styles.icon, "paperflite-add")}></i>
          <div className={cx(styles.name, styles.add)}>New Filter</div>
        </div>
      </Reveal>
    </div>
  );
};

interface FilterComponentProps {
  filter: Filter;
  moduleType: ModuleType;
}

const FilterComponent: React.FC<FilterComponentProps> = (props) => {
  const { handleDeleteFilter } = React.useContext(FilterContext);

  const springs = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
  });
  return (
    <animated.div style={{ ...springs }} className={styles.filterContainer}>
      <PrimaryFilter filter={props.filter} moduleType={props.moduleType} />
      <SubFilter filter={props.filter} />
      <InputBox filter={props.filter} />
      <i
        onClick={() => handleDeleteFilter(_.get(props, "filter.id", ""))}
        className={cx("paperflite-close", styles.closeIcon)}
      />
    </animated.div>
  );
};
interface Entity {
  name: string;
  value: string;
  filter_type: string;
}

export interface PrimaryFilterProps {
  filter: Filter;
  moduleType?: ModuleType;
}

const PrimaryFilter: React.FC<PrimaryFilterProps> = ({ filter , moduleType }) => {
  const { handlePrimaryFilterChange, PRIMARY_FILTERS } =
    React.useContext(FilterContext);

  const { items: customFields } = useStoreState("customFields", true);

  const [primaryFilters, setPrimaryFilters] =
    React.useState<(Entity | CustomField)[]>(PRIMARY_FILTERS);
  const [selectedFilter, setSelectedFilter] = React.useState(
    PRIMARY_FILTERS[0],
  );

  //to ensure custom fields are not displayed in the reports module.
  useEffect(() => {
    const isContentMap = moduleType === ModuleType.CONTENT_MAP;
  
    if (_.size(customFields) > 0 && isContentMap) {
      setPrimaryFilters([...PRIMARY_FILTERS, ...customFields]);
    } else {
      setPrimaryFilters([...PRIMARY_FILTERS]);
    }
  }, [customFields, PRIMARY_FILTERS, moduleType]);
  

  useEffect(() => {
    if (filter) {
      const propertyType = _.get(filter, "property.type");
      const activePrimaryFilter =
        propertyType === PropertyType.custom_field
          ? _.find(customFields, { id: _.get(filter, "property.fieldId") })
          : _.find(PRIMARY_FILTERS, { value: propertyType });

      if (activePrimaryFilter) {
        setSelectedFilter(activePrimaryFilter);
      }
    }
  }, [filter, customFields]);

  return (
    <Dropdown>
      <Dropdown.Toggle as="div" className={styles.dropDown}>
        {selectedFilter.name}
        <i className="paperflite-dropdown" />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {primaryFilters.map((item, index) => {
          const isActive = selectedFilter.name === item.name;
          return (
            <Dropdown.Item
              key={index}
              onClick={() =>
                handlePrimaryFilterChange(_.get(filter, "id", ""), item)
              }
            >
              <i
                className={cx(
                  styles.selectIcon,
                  {
                    [styles.activeSelect]: isActive,
                  },
                  isActive
                    ? "fa-solid fa-check-circle"
                    : "paperflite-radio-button-unselected",
                )}
              />
              <span className={styles.name}>{item.name}</span>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

interface SubFilterType {
  name: string;
  value: string;
  type: string[];
}

const SubFilter: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const { handleSubFilterChange } =
    React.useContext<FilterContextProps>(FilterContext);
  const [subFilters, setSubFilters] =
    React.useState<SubFilterType[]>(SUBFILTERS);

  useEffect(() => {
    getSubFilters();
  }, [filter]);

  const handleFilterClick = (item: SubFilterType) => {
    handleSubFilterChange(_.get(filter, "id", ""), item);
  };

  const getSubFilters = () => {
    if (filter) {
      const property = _.get(filter, "property.type");
      const extractedSubFilters = _.filter(SUBFILTERS, (subFilter) => {
        return _.includes(subFilter.type, property);
      });

      setSubFilters(extractedSubFilters);
    }
  };

  const getSubFilterName = (value: string) => {
    const extractedSubFilters = _.filter(SUBFILTERS, (subFilter) => {
      return _.includes(subFilter.value, value);
    });
    return extractedSubFilters[0].name;
  };

  return (
    <Dropdown className={styles.subFilterContainer}>
      <Dropdown.Toggle as="div" className={cx(styles.dropDown)}>
        {getSubFilterName(_.get(filter, "operator") || "is")}
        <i className="paperflite-dropdown" />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {subFilters.map((item) => {
          const isActive = _.get(filter, "operator") === item.value;
          return (
            <Dropdown.Item
              key={item.value}
              onClick={() => handleFilterClick(item)}
            >
              <i
                className={cx(
                  styles.selectIcon,
                  {
                    [styles.activeSelect]: isActive,
                  },
                  isActive
                    ? "fa-solid fa-check-circle"
                    : "paperflite-radio-button-unselected",
                )}
              />
              <span className={styles.name}>{item.name}</span>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export { ClusterView };
